export type SelectedCompany = {
  id: number
  name: string
}

export type SelectedCompaniesPayload = {
  scope: string
  companies: SelectedCompany[]
}

export function getSelectedCompaniesStorageScope() {
  return process.env.REACT_APP_BASE_URL ?? ''
}

export function parseSelectedCompanies(value: unknown): SelectedCompany[] {
  if (!Array.isArray(value)) return []

  return value.flatMap((item) => {
    if (typeof item !== 'object' || item == null) return []

    const id = Number((item as SelectedCompany).id)
    const name = String((item as SelectedCompany).name ?? '')

    if (!Number.isFinite(id) || !name) return []

    return [{id, name}]
  })
}

export function parseStoredSelectedCompanies(value: unknown): SelectedCompany[] {
  if (typeof value !== 'object' || value == null) return []

  const payload = value as Partial<SelectedCompaniesPayload>
  if (payload.scope !== getSelectedCompaniesStorageScope()) return []

  return parseSelectedCompanies(payload.companies)
}

export function shouldClearStaleSelectedCompanies(value: unknown): boolean {
  if (value == null) return false

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (typeof value !== 'object' || !('scope' in value)) {
    return false
  }

  const payload = value as Partial<SelectedCompaniesPayload>
  return typeof payload.scope === 'string' && payload.scope !== getSelectedCompaniesStorageScope()
}

export function parseRawSelectedCompaniesValue(raw: string): {
  companies: SelectedCompany[]
  shouldClear: boolean
} {
  let value: unknown

  try {
    value = JSON.parse(decodeURIComponent(raw))
  } catch {
    try {
      value = JSON.parse(raw)
    } catch {
      return {companies: [], shouldClear: false}
    }
  }

  const companies = parseStoredSelectedCompanies(value)

  return {
    companies,
    shouldClear: companies.length === 0 && shouldClearStaleSelectedCompanies(value),
  }
}

export function createSelectedCompaniesPayload(companies: SelectedCompany[]): SelectedCompaniesPayload {
  return {
    scope: getSelectedCompaniesStorageScope(),
    companies,
  }
}

export function serializeSelectedCompanies(companies: SelectedCompany[]) {
  return JSON.stringify(createSelectedCompaniesPayload(companies))
}
